import {
  MaterialDemandByDay,
  MaterialDemandEntry,
  getTotalValueForShippingTypes,
  getSelectedMaterialDemandEntries
} from '@domain/material-demand'

export type CombinedDemandKeys =
  | 'preOrders'
  | 'actuals'
  | 'merged'
  | 'predictions'
  | 'salesForecast'
  | 'schedules'
  | 'userOverrides'

export type CombinedDemandData = Record<CombinedDemandKeys, Record<string, number>>

const addNotNullValue = (acc: number | null, value: number | null): number | null => {
  if (value === null) return acc
  return (acc ?? 0) + value
}

const combineDemandDataForMaterials = (
  materialDemand: MaterialDemandEntry[]
): Record<CombinedDemandKeys, number | null> => {
  return materialDemand.reduce<Record<CombinedDemandKeys, number | null>>(
    (acc, entry) => {
      acc.predictions = addNotNullValue(
        acc.predictions,
        getTotalValueForShippingTypes(entry.predictions)
      )
      acc.merged = addNotNullValue(acc.merged, getTotalValueForShippingTypes(entry.merged))
      acc.actuals = addNotNullValue(acc.actuals, getTotalValueForShippingTypes(entry.actuals))
      acc.preOrders = addNotNullValue(acc.preOrders, getTotalValueForShippingTypes(entry.preOrders))
      acc.salesForecast = addNotNullValue(
        acc.salesForecast,
        getTotalValueForShippingTypes(entry.salesForecast)
      )
      acc.userOverrides = addNotNullValue(
        acc.userOverrides,
        getTotalValueForShippingTypes(entry.userOverrides)
      )
      acc.schedules = addNotNullValue(acc.schedules, getTotalValueForShippingTypes(entry.schedules))
      return acc
    },
    {
      predictions: null,
      merged: null,
      actuals: null,
      preOrders: null,
      salesForecast: null,
      schedules: null,
      userOverrides: null
    }
  )
}

/**
 * Combines all selected materials data daily for the demand chart.
 */
export const getCombinedDemandDataDaily = (
  materialDemandByDay: MaterialDemandByDay,
  materialIds?: number[]
): CombinedDemandData => {
  return Object.entries(materialDemandByDay).reduce<CombinedDemandData>(
    (acc, [day, materialDemandById]) => {
      const selectedEntries = getSelectedMaterialDemandEntries(materialDemandById, materialIds)
      const data = combineDemandDataForMaterials(selectedEntries)
      if (data.predictions !== null) acc.predictions[day] = data.predictions
      if (data.merged !== null) acc.merged[day] = data.merged
      if (data.actuals !== null) acc.actuals[day] = data.actuals
      if (data.preOrders !== null) acc.preOrders[day] = data.preOrders
      if (data.salesForecast !== null) acc.salesForecast[day] = data.salesForecast
      if (data.userOverrides !== null) acc.userOverrides[day] = data.userOverrides
      if (data.schedules !== null) acc.schedules[day] = data.schedules
      return acc
    },
    {
      predictions: {},
      merged: {},
      actuals: {},
      preOrders: {},
      salesForecast: {},
      userOverrides: {},
      schedules: {}
    }
  )
}
